/**
 * Authenticated API client helper
 *
 * Wraps fetch() with automatic Authorization: Bearer <idToken> header
 * when the user is signed in. Falls back to unauthenticated for public
 * endpoints (assessment page, diagnostic).
 *
 * On 401/403: fires the registered auth-error handler (set by AuthProvider)
 * which signs the user out and redirects to /login?reason=session_expired.
 *
 * Usage:
 *   import { apiFetch } from '../lib/apiClient'
 *   const data = await apiFetch('/dashboard/runs', idToken)
 */

const API_BASE = import.meta.env.VITE_API_URL
  || 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'

// ---------------------------------------------------------------------------
// Auth-error callback — registered by AuthProvider on mount so apiFetch can
// trigger a sign-out + redirect without importing Firebase directly.
// ---------------------------------------------------------------------------
let _onAuthError: (() => void) | null = null

export function setAuthErrorHandler(fn: () => void): void {
  _onAuthError = fn
}

export class AuthError extends Error {
  constructor() { super('Session expired') }
}

export async function apiFetch<T = unknown>(
  path: string,
  idToken: string | null,
  options?: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> ?? {}),
  }

  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (res.status === 401 || res.status === 403) {
    _onAuthError?.()
    throw new AuthError()
  }

  if (!res.ok) {
    throw new Error(`API error ${res.status}: ${await res.text()}`)
  }

  return res.json()
}
